// Google Analytics 4 Integration for Synkro
import { hasAnalyticsConsent } from './cookieConsent';

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const isProduction = import.meta.env.PROD;

let ReactGA = null;
const getGA = async () => {
  if (!ReactGA) {
    const mod = await import('react-ga4');
    ReactGA = mod.default;
  }
  return ReactGA;
};

export const initGA = async () => {
  if (!isProduction || !GA_MEASUREMENT_ID || !hasAnalyticsConsent()) return;

  const ga = await getGA();
  ga.initialize(GA_MEASUREMENT_ID, {
    gaOptions: { anonymizeIp: true }
  });
};

export const trackPageView = async (path, title) => {
  if (!isProduction || !hasAnalyticsConsent()) return;
  const ga = await getGA();
  ga.send({ hitType: 'pageview', page: path, title });
};

const trackEvent = async (eventName, params = {}) => {
  if (!isProduction) console.debug('[analytics]', eventName, params);
  if (!isProduction || !hasAnalyticsConsent()) return;
  const ga = await getGA();
  ga.event(eventName, params);
};

// ========================================
// USER EVENTS
// ========================================

/**
 * Track user signup
 * @param {string} method - 'google', 'microsoft', 'apple', 'email'
 */
export const trackUserSignup = (method) => {
  trackEvent('user_signup', {
    method,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track user login
 * @param {string} method - 'google', 'microsoft', 'apple', 'email'
 */
export const trackUserLogin = (method) => {
  trackEvent('user_login', {
    method,
    timestamp: new Date().toISOString()
  });
};

// ========================================
// EVENT EVENTS (Synkro events, not GA events)
// ========================================

/**
 * Track event creation flow started (event type chosen, before dates picked).
 * No event_id — funnel step preceding creation, not tied to a specific event yet.
 * @param {string} eventType - Type of event label (e.g., '🍽️ Dîner/Soirée') or 'other'
 */
export const trackEventCreationStarted = (eventType) => {
  trackEvent('event_creation_started', {
    event_type: eventType,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track event creation
 * @param {string} eventType - Type of event (e.g., 'apero', 'reunion', 'anniversaire')
 * @param {number} participantCount - Expected number of participants
 * @param {boolean} hasBudgetVote - Whether budget voting is enabled
 * @param {string|null} plan - Organizer plan at creation time ('gratuit', 'pro', 'enterprise')
 */
export const trackEventCreated = (eventType, participantCount = 0, hasBudgetVote = false, plan = null) => {
  trackEvent('event_created', {
    event_type: eventType,
    participant_count: participantCount,
    has_budget_vote: hasBudgetVote,
    ...(plan ? { plan } : {}),
    timestamp: new Date().toISOString()
  });
};

/**
 * Track event published/shared
 * @param {string} eventId - The event ID
 * @param {string} shareMethod - 'whatsapp', 'sms', 'email', 'copy'
 */
export const trackEventPublished = (eventId, shareMethod) => {
  trackEvent('event_published', {
    event_id: eventId,
    share_method: shareMethod,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track event completed (date confirmed)
 * @param {string} eventId - The event ID
 * @param {number} totalParticipants - Total participants who responded
 */
export const trackEventCompleted = (eventId, totalParticipants = 0) => {
  trackEvent('event_completed', {
    event_id: eventId,
    total_participants: totalParticipants,
    timestamp: new Date().toISOString()
  });
};

// ========================================
// PARTICIPANT EVENTS
// ========================================

/**
 * Track participant response (vote submitted)
 * @param {string} eventId - The event ID
 * @param {number} datesSelected - Number of dates selected
 * @param {boolean} hasEmail - Whether participant provided email
 */
export const trackParticipantResponded = (eventId, datesSelected = 0, hasEmail = false) => {
  trackEvent('participant_responded', {
    event_id: eventId,
    dates_selected: datesSelected,
    has_email: hasEmail,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track participant registration via public link
 * @param {string} eventId - The event ID
 */
export const trackParticipantRegistered = (eventId) => {
  trackEvent('participant_registered', {
    event_id: eventId,
    timestamp: new Date().toISOString()
  });
};

// ========================================
// CONVERSION EVENTS
// ========================================

/**
 * Track upgrade button click (initiated)
 * @param {string} plan - 'pro' or 'enterprise'
 * @param {string} source - Where the upgrade was initiated from
 */
export const trackUpgradeInitiated = (plan, source = 'pricing') => {
  trackEvent('upgrade_initiated', {
    plan,
    source,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track paywall modal shown
 * @param {string} source - Where the paywall was triggered (e.g. 'dashboard', 'create_blocked')
 * @param {string} plan - User's current plan when paywall shown
 */
export const trackPaywallShown = (source, plan = 'gratuit') => {
  trackEvent('paywall_shown', {
    source,
    plan,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track paywall upgrade button click
 * @param {string} source - Where the paywall was shown
 */
export const trackPaywallUpgradeClicked = (source) => {
  trackEvent('paywall_upgrade_clicked', {
    source,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track paywall dismiss without upgrade
 * @param {string} source - Where the paywall was shown
 */
export const trackPaywallDismissed = (source) => {
  trackEvent('paywall_dismissed', {
    source,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track checkout started
 * @param {string} plan - 'pro' or 'enterprise'
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @param {number} value - Price in euros
 */
export const trackCheckoutStarted = (plan, billingPeriod, value) => {
  trackEvent('checkout_started', {
    plan,
    billing_period: billingPeriod,
    value,
    currency: 'EUR',
    timestamp: new Date().toISOString()
  });
};

/**
 * Track successful upgrade/payment
 * @param {string} plan - 'pro' or 'enterprise'
 * @param {number} value - Price in euros
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 */
export const trackUpgradeCompleted = async (plan, value, billingPeriod = 'monthly') => {
  if (!isProduction || !hasAnalyticsConsent()) return;

  const ga = await getGA();
  ga.event('purchase', {
    transaction_id: `txn_${Date.now()}`,
    value,
    currency: 'EUR',
    items: [{
      item_id: `plan_${plan}`,
      item_name: `Synkro ${plan}`,
      price: value,
      quantity: 1
    }]
  });

  trackEvent('upgrade_completed', {
    plan,
    value,
    billing_period: billingPeriod,
    currency: 'EUR',
    timestamp: new Date().toISOString()
  });
};

// ========================================
// ENGAGEMENT EVENTS
// ========================================

/**
 * Track calendar sync
 * @param {string} calendarType - 'google', 'outlook', 'apple', 'ics'
 */
export const trackCalendarSynced = (calendarType) => {
  trackEvent('calendar_synced', {
    calendar_type: calendarType,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track PWA installation
 */
export const trackPWAInstalled = () => {
  trackEvent('pwa_installed', {
    timestamp: new Date().toISOString()
  });
};

/**
 * Track invite share action (link copied or shared via a platform).
 * Unified schema — replaces the old (eventId, shareType) signature.
 * @param {string} shareType - 'whatsapp' | 'messenger' | 'linkedin' | 'email' | 'sms' | 'copy'
 * @param {string} context - 'organizer' | 'admin' — surface where the share happened
 */
export const trackShareLinkCopied = (shareType, context) => {
  trackEvent('share_link_copied', {
    share_type: shareType,
    context,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track invite page opened by a participant (Participant.jsx mount, once per fetch).
 * No event_id — matches funnel schema for anonymous invitees.
 * @param {string} eventType - Event type label from the loaded event
 */
export const trackInviteOpened = (eventType) => {
  trackEvent('invite_opened', {
    event_type: eventType,
    timestamp: new Date().toISOString()
  });
};

/**
 * TODO: Not yet wired — no post-vote CTA exists in Participant.jsx (backlog UX #3).
 * Exported so the funnel schema is complete; call once the CTA lands.
 */
export const trackParticipantCtaClicked = () => {
  trackEvent('participant_cta_clicked', {
    timestamp: new Date().toISOString()
  });
};

// ========================================
// WEB VITALS
// ========================================

/**
 * Report Web Vitals to GA4
 * Call this after cookie consent is given
 */
export const reportWebVitals = async () => {
  if (!isProduction || !hasAnalyticsConsent()) return;

  try {
    const { onCLS, onFID, onFCP, onLCP, onTTFB } = await import('web-vitals');

    const sendToGA = ({ name, delta, id }) => {
      ReactGA.event({
        category: 'Web Vitals',
        action: name,
        value: Math.round(name === 'CLS' ? delta * 1000 : delta),
        label: id,
        nonInteraction: true,
      });
    };

    onCLS(sendToGA);
    onFID(sendToGA);
    onFCP(sendToGA);
    onLCP(sendToGA);
    onTTFB(sendToGA);

    console.log('[GA4] Web Vitals reporting enabled');
  } catch (error) {
    console.warn('[GA4] Web Vitals reporting failed:', error);
  }
};

// Export all functions as default object for convenience
export default {
  initGA,
  trackPageView,
  trackUserSignup,
  trackUserLogin,
  trackEventCreationStarted,
  trackEventCreated,
  trackEventPublished,
  trackEventCompleted,
  trackParticipantResponded,
  trackParticipantRegistered,
  trackUpgradeInitiated,
  trackPaywallShown,
  trackPaywallUpgradeClicked,
  trackPaywallDismissed,
  trackCheckoutStarted,
  trackUpgradeCompleted,
  trackCalendarSynced,
  trackPWAInstalled,
  trackShareLinkCopied,
  trackInviteOpened,
  trackParticipantCtaClicked,
  reportWebVitals
};
